import { styled } from '@/styles/stitches.config';
import { motion } from 'framer-motion';
import { useContext } from 'react';
import { isPresent } from 'ts-is-present';
import { ChartContext, SunriseChartData } from '.';
import { Label } from '../label';

type LegendProps = {
  data?: SunriseChartData;
};

type LegendGroupProps = {
  isTopAligned: boolean;
  hasRotatedIcons: boolean;
  group: SunriseChartData[number];
};

const Root = styled('figcaption', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  color: 'var(--color-text-light)',
});

export function Legend(props: LegendProps) {
  const { data } = props;

  if (!data) return null;

  return (
    <Root>
      {data.filter(isPresent).map((group, groupIdx) => (
        <LegendGroup
          group={group}
          key={group.label}
          isTopAligned={groupIdx === 0}
          hasRotatedIcons={data.length > 1}
        />
      ))}
    </Root>
  );
}

const LegendGroupRoot = styled('div', {
  position: 'absolute',
  paddingBlock: '$3',
  paddingInline: '$3',

  variants: {
    isTopAligned: {
      true: {
        top: 0,
        left: 0,
      },
      false: {
        bottom: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'flex-end',
      },
    },
  },
});

const Heading = styled(Label, {
  display: 'inline-block',
  width: '35ch',
  maxWidth: 'fit-content',
  marginBlockStart: 0,
  marginBlockEnd: '$2',
  fontWeight: '$medium',
  position: 'absolute',
  bottom: '100%',

  '@bp4': {
    position: 'static',
  },
});

const List = styled(motion.dl, {
  margin: 0,
});

const Value = styled('dd', {
  display: 'grid',
  alignItems: 'center',
  margin: 0,
  marginInlineStart: '0.25em',
});

const LegendItem = styled(motion.div, {
  display: 'flex',
  marginBlockEnd: '$1',
});

const Term = styled('dt', {
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: 'repeat(2, auto)',
  gridGap: '$2',
});

const LegendSymbol = styled('span', {
  '--r': '0.5em',

  display: 'inline-block',
  width: 'calc(2 * var(--r))',
  height: 'var(--r)',
  transformOrigin: '50% 50%',
  transition: 'transform 0.2s ease-in-out',

  variants: {
    hasRotatedIcons: {
      true: {
        transform: 'rotate(-45deg)',
      },
      false: {
        transform: 'rotate(0deg)',
      },
    },
    isTopAligned: {
      true: {
        borderRadius: '1.5em 1.5em 0 0',
      },
      false: {
        borderRadius: '0 0 1.5em 1.5em',
      },
    },
  },
});

function LegendGroup(props: LegendGroupProps) {
  const { isTopAligned, group, hasRotatedIcons } = props;

  const { formatValue, getFill, getStroke } = useContext(ChartContext);

  if (!group) return null;

  return (
    <LegendGroupRoot isTopAligned={isTopAligned}>
      {group.label && <Heading>{group.label}</Heading>}

      <List initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {group.data.map((d, dIdx) => (
          <LegendItem key={group.label + d.label} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Term>
              <LegendSymbol
                hasRotatedIcons={hasRotatedIcons}
                isTopAligned={isTopAligned}
                css={{
                  border: dIdx === 1 ? `0.1rem dashed ${getStroke(dIdx === 1)}` : 0,
                  backgroundColor: getFill(dIdx === 0, isTopAligned),
                }}
              />
              <Label>{d.label}</Label>
            </Term>
            <Value>
              <Label>
                {d.isApproximatValue && '~'}
                {formatValue(d.value)}
              </Label>
            </Value>
          </LegendItem>
        ))}
      </List>
    </LegendGroupRoot>
  );
}
